import type {
  SprintItem,
  TaskItem,
  SprintCompletionResult,
  CreateSprintPayload,
  UpdateSprintPayload,
  StartSprintPayload,
  CompleteSprintPayload,
  SprintTasksPayload,
} from '@hygge/shared';
import { api } from './api';

export async function getSprints(
  boardId: string,
  status?: string,
): Promise<SprintItem[]> {
  const { data } = await api.get<SprintItem[]>(
    `/boards/${boardId}/sprints`,
    { params: status ? { status } : undefined },
  );
  return data;
}

export async function getActiveSprint(
  boardId: string,
): Promise<SprintItem | null> {
  const { data } = await api.get<SprintItem | null>(
    `/boards/${boardId}/sprints/active`,
  );
  return data || null;
}

export async function getSprint(id: string): Promise<SprintItem> {
  const { data } = await api.get<SprintItem>(`/sprints/${id}`);
  return data;
}

export async function getBacklog(boardId: string): Promise<TaskItem[]> {
  const { data } = await api.get<TaskItem[]>(
    `/boards/${boardId}/backlog`,
  );
  return data;
}

export async function createSprint(
  boardId: string,
  payload: CreateSprintPayload,
): Promise<SprintItem> {
  const { data } = await api.post<SprintItem>(
    `/boards/${boardId}/sprints`,
    payload,
  );
  return data;
}

export async function updateSprint(
  id: string,
  payload: UpdateSprintPayload,
): Promise<SprintItem> {
  const { data } = await api.patch<SprintItem>(`/sprints/${id}`, payload);
  return data;
}

export async function deleteSprint(id: string): Promise<void> {
  await api.delete(`/sprints/${id}`);
}

export async function startSprint(
  id: string,
  payload: StartSprintPayload,
): Promise<SprintItem> {
  const { data } = await api.post<SprintItem>(
    `/sprints/${id}/start`,
    payload,
  );
  return data;
}

export async function completeSprint(
  id: string,
  payload: CompleteSprintPayload,
): Promise<SprintCompletionResult> {
  const { data } = await api.post<SprintCompletionResult>(
    `/sprints/${id}/complete`,
    payload,
  );
  return data;
}

export async function addTasksToSprint(
  sprintId: string,
  payload: SprintTasksPayload,
): Promise<{ added: number }> {
  const { data } = await api.post<{ added: number }>(
    `/sprints/${sprintId}/tasks`,
    payload,
  );
  return data;
}

export async function removeTasksFromSprint(
  sprintId: string,
  payload: SprintTasksPayload,
): Promise<{ removed: number }> {
  const { data } = await api.delete<{ removed: number }>(
    `/sprints/${sprintId}/tasks`,
    { data: payload },
  );
  return data;
}

export async function reorderSprints(
  boardId: string,
  sprintIds: string[],
): Promise<{ success: boolean; updated: number }> {
  const { data } = await api.patch<{ success: boolean; updated: number }>(
    `/boards/${boardId}/sprints/reorder`,
    { sprintIds },
  );
  return data;
}

export async function reorderSprintIssues(
  sprintId: string,
  taskIds: string[],
): Promise<{ success: boolean; updated: number }> {
  const { data } = await api.patch<{ success: boolean; updated: number }>(
    `/sprints/${sprintId}/issues/reorder`,
    { taskIds },
  );
  return data;
}
